import { useMemo } from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { getDateAggregationKey } from '../../../utils/getDateAggregationKey';
import { deriveQuantArcBacktestSeries, type QuantArcBacktestRow } from './backtestSeries';
import { WidgetWrapper } from './WidgetWrapper';
import { downsampleWithMarkers } from '../../../utils/performance';

interface Props {
    data: QuantArcBacktestRow[];
}

function formatNumber(value: unknown, digits = 2): string {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return numeric.toFixed(digits);
}

function CustomTooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;

    const pnlEntry = payload.find((p: any) => p.dataKey === 'pnl');
    if (!pnlEntry) return null;

    const data = pnlEntry.payload;
    const formattedDate = getDateAggregationKey(label, 'datetime');

    return (
        <div style={{
            background: 'rgba(30, 30, 30, 0.95)',
            border: '1px solid var(--surface-glass-border)',
            borderRadius: '10px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            padding: '12px',
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
            pointerEvents: 'none',
            minWidth: '200px'
        }}>
            <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontWeight: '600', fontSize: '0.75rem' }}>
                {formattedDate}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ opacity: 0.8 }}>Cumulative PnL:</span>
                    <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem', fontFamily: 'monospace' }}>
                        {formatNumber(pnlEntry.value, 2)}
                    </span>
                </div>

                {data.trade_pnl != null && (
                    <div style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Latest Trade:</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ color: data.trade_pnl >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)', fontWeight: 'bold' }}>
                                {data.trade_pnl > 0 ? '+' : ''}{formatNumber(data.trade_pnl, 2)}
                            </span>
                        </div>
                        {data.entry_price != null && data.exit_price != null && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: '2px' }}>
                                {formatNumber(data.entry_price, 4)} → {formatNumber(data.exit_price, 4)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export function QuantArcEquityWidget({ data }: Props) {
    const series = useMemo(() => {
        const raw = deriveQuantArcBacktestSeries(data);
        if (raw.length > 1000) {
            return downsampleWithMarkers(raw, 800, 'pnl', ['entryMarker', 'exitMarker', 'flipMarker']);
        }
        return raw;
    }, [data]);

    if (!series.length) {
        return (
            <WidgetWrapper title="📈 Performance">
                <div style={{ color: 'var(--text-muted)', padding: '1rem' }}>No QuantArc performance data available.</div>
            </WidgetWrapper>
        );
    }

    return (
        <WidgetWrapper title="📈 Performance">
            <div style={{ width: '100%', height: '100%', padding: '0.5rem 0' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="var(--accent-success)" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="var(--accent-success)" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                        <XAxis 
                            dataKey="time" 
                            tickFormatter={(value) => getDateAggregationKey(value)} 
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }} 
                            axisLine={false}
                            tickLine={false}
                            minTickGap={30}
                        />
                        <YAxis 
                            yAxisId="pnl" 
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }} 
                            tickFormatter={(value) => formatNumber(value, 0)}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            animationDuration={0}
                            cursor={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1 }}
                        />
                        <ReferenceLine yAxisId="pnl" y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" />
                        <Area
                            yAxisId="pnl"
                            type={series.length > 800 ? "linear" : "monotone"}
                            dataKey="pnl"
                            name="pnl"
                            stroke="var(--accent-success)"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorEquity)"
                            isAnimationActive={false}
                            activeDot={{ r: 4, strokeWidth: 0 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </WidgetWrapper>
    );
}
